import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "store/Store";
import { DataScenario } from "types/apis/cxEditor";

export interface DataScenariosReducerState {
  // Key format: `${pilotId}-${fuelType}-${locale}`
  // Value: Array of all DataScenario objects for that pilot/fuelType/locale combination
  scenariosByKey: Record<string, DataScenario[]>;
  loading: Record<string, boolean>; // Track loading state per key
  error: Record<string, string | null>; // Track errors per key
}

const defaultState: DataScenariosReducerState = {
  scenariosByKey: {},
  loading: {},
  error: {},
};

/**
 * Generate a key for storing scenarios based on pilot, fuel type, and locale
 */
export function getScenariosKey(pilotId: string, fuelType: string, locale: string): string {
  return `${pilotId}-${fuelType}-${locale}`;
}

export const dataScenariosSlice = createSlice({
  name: 'dataScenarios',
  initialState: defaultState,
  reducers: {
    setDataScenarios: (
      state,
      action: PayloadAction<{
        pilotId: string;
        fuelType: string;
        locale: string;
        dataScenarios: DataScenario[];
      }>
    ) => {
      const { pilotId, fuelType, locale, dataScenarios } = action.payload;
      const key = getScenariosKey(pilotId, fuelType, locale);
      state.scenariosByKey[key] = dataScenarios;
      state.loading[key] = false;
      state.error[key] = null;
    },
    setDataScenariosLoading: (
      state,
      action: PayloadAction<{
        pilotId: string;
        fuelType: string;
        locale: string;
        loading: boolean;
      }>
    ) => {
      const { pilotId, fuelType, locale, loading } = action.payload;
      const key = getScenariosKey(pilotId, fuelType, locale);
      state.loading[key] = loading;
      if (loading) {
        state.error[key] = null; // Clear error when starting to load
      }
    },
    setDataScenariosError: (
      state,
      action: PayloadAction<{
        pilotId: string;
        fuelType: string;
        locale: string;
        error: string | null;
      }>
    ) => {
      const { pilotId, fuelType, locale, error } = action.payload;
      const key = getScenariosKey(pilotId, fuelType, locale);
      state.error[key] = error;
      state.loading[key] = false;
    },
    clearDataScenarios: (
      state,
      action: PayloadAction<{
        pilotId: string;
        fuelType: string;
        locale: string;
      }>
    ) => {
      const { pilotId, fuelType, locale } = action.payload;
      const key = getScenariosKey(pilotId, fuelType, locale);
      delete state.scenariosByKey[key];
      delete state.loading[key];
      delete state.error[key];
    },
    clearAllDataScenarios: (state) => {
      state.scenariosByKey = {};
      state.loading = {};
      state.error = {};
    },
  },
});

export const {
  setDataScenarios,
  setDataScenariosLoading,
  setDataScenariosError,
  clearDataScenarios,
  clearAllDataScenarios,
} = dataScenariosSlice.actions;

// Selectors
export const selectDataScenariosByKey = (
  state: RootState,
  pilotId: string,
  fuelType: string,
  locale: string
): DataScenario[] => {
  const key = getScenariosKey(pilotId, fuelType, locale);
  return (state.dataScenarios as DataScenariosReducerState).scenariosByKey[key] || [];
};

export const selectDataScenariosLoading = (
  state: RootState,
  pilotId: string,
  fuelType: string,
  locale: string
): boolean => {
  const key = getScenariosKey(pilotId, fuelType, locale);
  return (state.dataScenarios as DataScenariosReducerState).loading[key] || false;
};

export const selectDataScenariosError = (
  state: RootState,
  pilotId: string,
  fuelType: string,
  locale: string
): string | null => {
  const key = getScenariosKey(pilotId, fuelType, locale);
  return (state.dataScenarios as DataScenariosReducerState).error[key] || null;
};

/**
 * Get filtered scenarios for a specific template and channel
 * Returns array of full DataScenario objects
 * Filters by:
 * 1. templateName matches templateKey
 * 2. productType matches channel
 */
export const selectScenariosForTemplate = (
  state: RootState,
  pilotId: string,
  fuelType: string,
  locale: string,
  templateKey: string,
  channel: string
): DataScenario[] => {
  const allScenarios = selectDataScenariosByKey(state, pilotId, fuelType, locale);
  const shouldDebugLog =
    typeof window !== 'undefined' &&
    // Enable by setting: sessionStorage.setItem('cx_debug_scenarios','1')
    window.sessionStorage?.getItem('cx_debug_scenarios') === '1';
  
  if (!allScenarios.length) {
    if (shouldDebugLog) {
      console.log('[selectScenariosForTemplate] No scenarios in Redux for key:', {
        pilotId,
        fuelType,
        locale,
        templateKey,
        channel
      });
    }
    return [];
  }

  // Debug: Log all scenarios before filtering for Web channel
  if (shouldDebugLog && channel?.toLowerCase() === 'web') {
    console.log('[selectScenariosForTemplate] All scenarios before filtering (Web):', {
      templateKey,
      channel,
      allScenariosCount: allScenarios.length,
      allScenarios: allScenarios.map(ds => ({
        templateName: ds.templateName,
        productType: ds.productType,
        scenarioId: ds.scenarioId,
        matchesTemplate: ds.templateName === templateKey,
        matchesProduct: (ds.productType || '').toLowerCase() === (channel || 'Email').toLowerCase()
      }))
    });
  }

  // Filter scenarios by template and product type (channel)
  const filtered = allScenarios.filter((ds: DataScenario) => {
    // Check 1: templateName must match templateKey
    const matchesTemplate = ds.templateName === templateKey;
    
    // Check 2: productType must match channel
    const matchesProduct = (ds.productType || '').toLowerCase() === (channel || 'Email').toLowerCase();
    
    // Debug logging for Web channel (very noisy; keep behind flag)
    if (shouldDebugLog && channel?.toLowerCase() === 'web' && !matchesTemplate) {
      console.log('[selectScenariosForTemplate] Template mismatch for Web:', {
        templateKey,
        templateName: ds.templateName,
        productType: ds.productType,
        scenarioId: ds.scenarioId,
        matchesTemplate,
        matchesProduct
      });
    }
    
    return matchesTemplate && matchesProduct;
  });
  
  // Debug logging
  if (shouldDebugLog && channel?.toLowerCase() === 'web') {
    console.log('[selectScenariosForTemplate] Web channel filtering:', {
      templateKey,
      channel,
      allScenariosCount: allScenarios.length,
      filteredCount: filtered.length,
      allScenarios: allScenarios.map(ds => ({
        templateName: ds.templateName,
        productType: ds.productType,
        scenarioId: ds.scenarioId
      }))
    });
  }

  // Sort: default scenarios first, then by scenarioDisplayName
  filtered.sort((a, b) => {
    if (a.scenarioId === b.scenarioId) return 0;
    if (a.defaultScenario && !b.defaultScenario) return -1;
    if (!a.defaultScenario && b.defaultScenario) return 1;
    return (a.scenarioDisplayName || a.scenarioId).localeCompare(b.scenarioDisplayName || b.scenarioId);
  });

  return filtered;
};

